import { action, observable, computed, when, observe } from 'mobx';
import uuid from 'uuid/v4';

import {
  client as apolloClient,
  subscriptionStatusObservable,
} from 'core/client';
import { analytics } from 'core/analytics';
import { AppManager } from 'core/appManager';
import { Variables } from 'core/query';
import { Input } from 'core/mutation';
import { clientStore } from 'apps/client/app';
import { throttle } from 'throttle-debounce';

import {
  CreateMessageMutation,
  UpdateMessageMutation,
  DeleteMessageMutation,
  ContextConversationQuery,
  ConversationQueryWithParticipants,
  MessageCreatedSubscription,
  MessageUpdatedSubscription,
  MessageDeletedSubscription,
  CreateAnnotationMutation,
  DeleteAnnotationMutation,
  AnnotationsQuery,
  PatchSubscription,
  ActiveParticipantStatusQuery,
  ParticipantStatusSubscription,
  ReadMessageMutation,
} from './gql';

import BidirectionalQueryManager from './BidirectionalQueryManager';

const messagesPageSize = 30;

const timestampToDate = timestamp => {
  if (typeof timestamp === 'string') {
    return Date.parse(timestamp);
  }
  return new Date(timestamp);
};

// NOTE (david): This function is used to update lastXMessage "pointers" by checking
// if new messages are newer. This needs to be checked because network can
// alter the order of operations. Furthermore, given two equally newer messages
// it returns the one with more data, this is useful when we are comparing
// locally constructed messages and server messages. Server messages have more
// data and are preferred. (Particularly id)
const getNewestMessageBetween = (message1, message2) => {
  if (!message1) return message2;
  if (!message2) return message1;

  // If they are different messages, return newest
  if (message1.tempKey !== message2.tempKey) {
    // if (message1.timestamp && message2.timestamp) {
    const date1 = timestampToDate(message1.timestamp);
    const date2 = timestampToDate(message2.timestamp);
    if (date1 > date2) return message1;
    if (date2 > date1) return message2;
    // }
  }

  // Else they have equal timestamp, or they are the same message.
  // Then return the object with more data. Since more data means
  // it's probably been constructed later.
  if (Object.keys(message1).length > Object.keys(message2).length)
    return message1;
  return message2;
};

export class ContextAppManager extends AppManager {
  static registryName = 'ContextAppManager';
  // annotations / messages
  @observable messages = [];
  @observable participants = [];
  @observable annotations = [];
  @observable members = {};
  @observable memberStatus = {};
  @observable firstMessageIndex = 0;
  @observable lastMessageIndex = 0;
  @observable lastUnreadMessageIndex = 0;
  @observable failedMessages = [];

  @observable lastReadMessage = undefined;
  @observable lastMessage = undefined;
  @observable sendAsId = null;
  @observable initialTopMostItemIndex = 99999;
  @observable unreadCount = -1;
  annotationRetryTime = 0;
  associatedSpaceId = undefined;

  @observable viewerControl = null;
  @observable diffFileManager = null;
  @observable loading = false;

  constructor(parent, init = {}) {
    super(parent, init);
    this.variables = new Variables({
      contextId: () => this.context?.id,
      resourceId: this.resourceId,
      referenceId: () =>
        this.contextAppHolder.rfqApp?.id || this.contextAppHolder.order?.id,
    });

    this.diffFileManager = init.diffFileManager;

    this.variables.onChange(['contextId', 'resourceId'], this.NewVariables);

    // observe the client status
    // Not sure why we don't just add this to variables and 'onChange' above??
    this.subscriptionStatusDisposer = observe(
      subscriptionStatusObservable,
      throttle(3000, change => {
        // check if it is reconnection
        if (
          change.oldValue === 'disconnected' &&
          change.newValue === 'connected'
        ) {
          // subscription reconnected query all the stuff again
          // which will also trigger re-subscribe
          this.queryAll();
        }
      }),
    );
    this.disposers.push(this.subscriptionStatusDisposer);
  }

  @computed get context() {
    return this.contextAppHolder?.contextApp;
  }

  @action SetViewerControl = viewerControl => {
    this.viewerControl = viewerControl;
  };

  /*
    CadFile API
  */

  @computed get viewerFile() {
    // this.contextAppHolder ->
    //  - RFQAppManager        (src/apps/rfq/rfqPage/index.js)
    //  - SharedFileAppManager (src/apps/share/main.js)
    //  - FileViewerManager    (src/apps/project/tabs/file/filePage.js)
    if (this.contextAppHolder?.contribFile?.file)
      return this.contextAppHolder?.contribFile.file;

    if (this.contextAppHolder?.sharedFile?.revision?.file)
      return this.contextAppHolder?.sharedFile?.revision?.file;

    if (this.contextAppHolder?.file) return this.contextAppHolder.file;

    // fix cannot find the file of rfq/sharedFile page.
    if (this.contextAppHolder?.viewerControl?.file) {
      return this.contextAppHolder.viewerControl.file;
    }
    // ...
    return null;
  }

  @computed get viewerConversion() {
    const conversion = this.viewerFile?.conversionGltfjson;
    return {
      conversion,
      status: conversion?.status || 'MISSING',
      reason: conversion?.reason || '',
      errorCode: conversion?.errorCode || '',
    };
  }

  @computed get viewerConversionProgress() {
    return {
      progress: this.contextAppHolder?.conversionProgress,
      steps: this.contextAppHolder?.conversionSteps,
    };
  }

  @computed get diffFile() {
    if (this.diffFileManager?.file) {
      return this.diffFileManager.file;
    }
    return null;
  }

  @computed get diffConversion() {
    const conversion = this.diffFile?.conversionGltfjson;
    return {
      conversion,
      status: conversion?.status || 'MISSING',
      reason: conversion?.reason || '',
      errorCode: conversion?.errorCode || '',
    };
  }

  @computed get diffConversionProgress() {
    return {
      progress: this.diffFileManager?.conversionProgress,
      steps: this.diffFileManager?.conversionSteps,
    };
  }

  NewVariables = () => {
    // reset messages and annotation
    this.messages = [];
    this.annotations = [];

    this.queryAll();
  };

  queryAll = () => {
    if (!clientStore.signedInProfile) return;

    this.QueryAnnotations();

    // FIXME
    if (
      this.contextAppHolder?.constructor.registryName === 'SharedFileAppManager'
    ) {
      this.QueryActiveParticipants();
    }
  };

  compareUserActivity = (activityA, activityB) => {
    if (activityA.status === 'ONLINE') return -1;
    if (activityB.status === 'ONLINE') return 1;
    return 0;
  };

  @computed get usersActivity() {
    // FIXME
    if (
      !this.contextAppHolder ||
      !this.contextAppHolder.sharedFile ||
      this.contextAppHolder?.constructor.registryName !== 'SharedFileAppManager'
    ) {
      return [];
    }

    const sharedFileAppManager = this.contextAppHolder;
    const userList = [...sharedFileAppManager.sharedFile?.groups?.participants];
    return userList
      .filter(({ profile }) => !!profile)
      .map(({ invite, profile }) => {
        let status = invite
          ? 'INVITED'
          : this.memberStatus[profile.user?.id]?.activityStatus || 'OFFLINE';
        if (this.clientStore?.signedInProfile.user.id === profile.user?.id) {
          status = 'ONLINE';
        }
        return { status, profile };
      });
  }

  @computed get revisions() {
    if (
      this.contextAppHolder?.constructor.registryName === 'SharedFileAppManager'
    ) {
      return this.contextAppHolder.revisionsConn?.arrayApp.apps;
    }
    return [];
  }

  // FIXME - change once Context has proper permissions
  @computed get annotationsAvailable() {
    // any one who signed in
    return !!this.clientStore?.signedInProfile;
  }

  // FIXME - change once Context has proper permissions
  @computed get chatAvailable() {
    // any one who signed in
    return !!this.clientStore?.signedInProfile;
  }

  @action SetSendAsId = id => {
    this.sendAsId = id;
  };

  @action handleCreatorProfile = profile => {
    if (!profile?.user?.id) return;

    if (profile.avatar) {
      profile.avatar.src = profile.avatar.url;
    }
    this.members[profile.user.id] = profile;
  };

  @action onSendMessageSuccess = serverMessage => {
    // add last message id base tempKey
    const lastMessageIndex = this.messages.findIndex(
      m => m.tempKey === serverMessage.tempKey,
    );

    if (lastMessageIndex > -1) {
      this.messages[lastMessageIndex] = serverMessage;
    }

    this.lastMessage = getNewestMessageBetween(serverMessage, this.lastMessage);
    this.lastReadMessage = getNewestMessageBetween(
      serverMessage,
      this.lastReadMessage,
    );
  };

  @action onResendMessageSuccess = serverMessage => {
    this.failedMessages = this.failedMessages.filter(
      failedMessage => serverMessage.tempKey !== failedMessage.tempKey,
    );
  };

  onSendMessageError = tempKey => {
    const messageFailed = this.failedMessages.find(
      failedMessage => failedMessage.tempKey === tempKey,
    );

    const failedMessageExists = Boolean(messageFailed);

    if (!failedMessageExists) {
      this.failedMessages.push({
        tempKey,
        resending: false,
      });
    }

    if (failedMessageExists) {
      messageFailed.resending = false;
    }
  };

  @action onUpdateMessageSuccess = ({ id, body }) => {
    const message = this.messages.find(item => item.id === id);

    message.body = body;
  };

  onUpdateMessageError = () => {};

  onDeleteMessageSuccess = () => {};

  onDeleteMessageError = () => {};

  @action handleMessageDeletion = message => {
    // Ignore null messages or self-messages of the "regular" type
    // As those will be handled through the mutation handler
    if (!message) {
      return;
    }

    const index = this.messages.findIndex(msg => msg.id === message.id);
    if (index !== -1) {
      this.messages.splice(index, 1);
    }
  };

  // order: handlePatch => handleMessageReceived
  @action handleMessageReceived = message => {
    // Ignore null messages or self-messages of the "regular" type
    // As those will be handled through the mutation handler
    if (!message) {
      return;
    }

    if (typeof message.createdBy !== 'string') {
      const { createdBy } = message;
      const { id, profile } = createdBy;

      if (!this.members[id]) {
        this.members[id] = profile;
      }
    }

    const updateAnnotationIndex = this.messages.findIndex(
      item => item.annotationId && item.annotationId === message.annotationId,
    );

    if (updateAnnotationIndex > -1) {
      this.messages[updateAnnotationIndex] = message;
      return;
    }

    const m = Object.assign({}, message);

    // Sanity checking for the migration to conversation API Changes.
    // Now, the referenceId will refer to the rfq

    if (message.referenceId === this.variables.referenceId) {
      const index = this.messages.findIndex(
        msg =>
          msg.tempKey &&
          msg.messageType === 'regular' &&
          msg.tempKey === message.tempKey,
      );

      if (index !== -1) this.messages[index] = m;
      else this.messages.push(m);

      this.lastMessage = getNewestMessageBetween(message, this.lastMessage);

      if (
        message.createdBy.id === this.clientStore?.signedInProfile?.user?.id
      ) {
        this.lastReadMessage = getNewestMessageBetween(
          message,
          this.lastReadMessage,
        );
      } else {
        this.unreadCount += 1;
      }
    } else {
      // if it is from other user
      if (
        message.createdBy.id !== this.clientStore?.signedInProfile?.user?.id
      ) {
        this.messages.push(message);
        return;
      }

      // check if the tempKey is from this client
      const index = this.messages.findIndex(
        msg =>
          msg.tempKey &&
          msg.messageType === 'regular' &&
          msg.tempKey === message.tempKey,
      );
      if (index !== -1) {
        // there is a message pending with this
        // add the replace the temp message
        this.messages[index] = message;
        return;
      }

      // it is the same user but not the same client
      this.messages.push(message);
    }
  };

  @action handleMessageUpdated = message => {
    if (!message) {
      return;
    }

    const updatedIndex = this.messages.findIndex(
      item => item.id === message.id,
    );

    if (updatedIndex > -1) {
      Object.assign(this.messages[updatedIndex], {
        ...message,
        updatedAt: message.updatedAt || new Date(),
      });
    }
  };

  onSendAnnotationSuccess = () => {
    this.loading = false;
    this.viewerControl?.logInfo('annotation sent successfully');
  };

  onSendAnnotationError = () => {
    this.loading = false;
    this.viewerControl?.reportError('error when sending annotation!!');
  };

  onQueryAnnotationsError = () => {
    this.viewerControl?.reportError('error when querying annotations!!');
  };

  onDeleteAnnotationSuccess = () => {
    this.loading = false;
    this.viewerControl?.logInfo('annotation deleted successfully');
  };

  onDeleteAnnotationError = () => {
    this.loading = false;
    this.viewerControl?.reportError('error when deleting annotation');
  };

  // - applyPatch is used when first loading the viewer to apply existing
  // patches (so annotations) to the viewer, and it is also used when we
  // receive a new patch (annotation) through the PatchSubscription
  // - a Patch is a json struct that contains an operation (like 'add' or
  // 'delete' currently) and a piece of data that contains the actual
  // change (change like in any kind of change could be sent through a
  // Patch, though currently we only send Annotations)
  @action applyPatch = patch => {
    if (!this.viewerControl) {
      this.viewerControl.reportError(
        new Error('cannot apply patch to null viewerControl'),
      );
      return;
    }

    const { viewer, cadFile } = this.viewerControl;
    if (viewer === null && !viewer?.isOk) {
      this.viewerControl.reportError(
        new Error('cannot apply patch to null viewer'),
      );
      return;
    }

    const { pmi } = cadFile;
    const { notePad, settings } = viewer;
    const { type, action: patchAction, data: patchData } = patch;
    // - we have to differentiate between the case where we received
    // 1.) the annotation that we ourselves added or 2.) an annotation
    // that someone else added
    // - this is why we have both id (the server generated annotationId),
    // and tempKey (a temporary key created inside the viewer)
    // - tempKey will be undefined when this is called when initially loading
    // the viewer and loading existing annotations, it is only defined when
    // we receive this patch through PatchSubscritpion
    const { id, tempKey, kind, timestamp, createdBy, body } = patchData;
    if (type !== 'annotation') {
      this.viewerControl.reportError(new Error('unknown action patch type'));
      return;
    }

    // - when a patch that is a Measurement fails to be applied, then I
    // measurementWithError will not be set to false, and the code that
    // applies the patch as Note will apply it instead, that way we will
    // not get a Measurement to show, but the a Pin will be there and can
    // be deleted
    let measurementWithError = true;

    // - kind can either be MEASUREMENT or NOTE
    if (kind === 'MEASUREMENT') {
      switch (patchAction) {
        case 'delete': {
          notePad.DeleteMeasurement(id);
          break;
        }
        case 'add': {
          const updateMeasurement = tempKey
            ? notePad.GetMeasurement(tempKey)
            : null;
          if (updateMeasurement) {
            updateMeasurement.annotationId = id;
            measurementWithError = false;
            break;
          } else {
            const existingMeasurement = notePad.GetMeasurement(id);
            if (existingMeasurement) {
              measurementWithError = false;
              break;
            }
            const { error } = notePad.RestoreMeasurement(patchData);
            if (error === 0) {
              measurementWithError = false;
            }
          }
          break;
        }
      }
    }
    if (kind === 'NOTE' || measurementWithError) {
      switch (patchAction) {
        case 'delete': {
          notePad.DeleteNote(id);
          break;
        }
        case 'add':
        default: {
          const updateNote = tempKey ? notePad.GetNote(tempKey) : null;
          if (updateNote) {
            updateNote.annotationId = id;
            updateNote.timestamp = timestamp;
            updateNote.userId = createdBy;
            updateNote.text = body;
          } else {
            const existingNote = notePad.GetNote(id);
            if (!existingNote) {
              const { error } = notePad.RestoreNote(patchData);
              if (error) {
                this.viewerControl.reportError(
                  new Error(`could not restore note: ${patchData}`),
                );
              }
            }
          }
        }
      }
    }

    notePad.reportWasmChange();
    pmi.reportWasmChange();
    settings.reportWasmChange();
  };

  @action syncAnnotationsAfterDeleted = patchData => {
    const deleteMessage = this.messages.findIndex(
      m => m.annotationId === patchData.id,
    );
    if (deleteMessage > -1) {
      const { id, annotationId } = this.messages[deleteMessage];
      this.evictAnnotationAndMessage(annotationId, id);
      this.messages.splice(deleteMessage, 1);
    }

    const deleteAnnotation = this.annotations.findIndex(
      a => a.id === patchData.id,
    );
    if (deleteAnnotation > -1) {
      this.annotations.splice(deleteAnnotation, 1);
    }
  };

  @action syncAnnotationsAfterAdded = patchData => {
    if (!patchData) {
      return;
    }

    this.annotations.push(patchData);
  };

  @action handlePatch = patch => {
    if (!patch) return;

    this.viewerControl?.logInfo(`patch received: ${patch?.data?.id}`);

    // add or delete the reply key based on the action
    switch (patch.action) {
      case 'delete': {
        if (patch.type === 'annotation') {
          this.syncAnnotationsAfterDeleted(patch.data);
        }
        break;
      }
      case 'add':
        if (patch.type === 'annotation') {
          this.syncAnnotationsAfterAdded(patch.data);
        }
        break;
      default:
    }
    try {
      this.applyPatch(patch);
    } catch (e) {
      analytics.logError(e);
    }
  };

  @action handleParticipantStatus = activityData => {
    this.memberStatus[activityData.userId] = activityData;
  };

  SubscribeParticipantStatus = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.participantStatusSubscription) {
      return;
    }

    this.participantStatusSubscription = new ParticipantStatusSubscription(
      this,
      {
        variables: {
          subscribeParticipantStatusInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      },
    );

    this.participantStatusSubscription.process(this.handleParticipantStatus);
    this.participantStatusSubscription.StartSubscription();
  };

  SubscribeMessageDeletion = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.messageDeletedSubscription) {
      this.messageDeletedSubscription.CompleteSubscription();
      this.messageDeletedSubscription = undefined;
    }

    if (!this.messageDeletedSubscription) {
      this.messageDeletedSubscription = new MessageDeletedSubscription(this, {
        variables: {
          subscribeMessageInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      });
      this.messageDeletedSubscription.process(this.handleMessageDeletion);
      this.messageDeletedSubscription.StartSubscription();
    }
  };

  SubscribeMessageCreation = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.messageCreatedSubscription) {
      this.messageCreatedSubscription.CompleteSubscription();
      this.messageCreatedSubscription = undefined;
    }
    if (!this.messageCreatedSubscription) {
      this.messageCreatedSubscription = new MessageCreatedSubscription(this, {
        variables: {
          subscribeMessageInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      });
      this.messageCreatedSubscription.process(this.handleMessageReceived);
      this.messageCreatedSubscription.StartSubscription();
    }
  };

  SubscribeMessageUpdate = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.messageUpdatedSubscription) {
      this.messageUpdatedSubscription.CompleteSubscription();
      this.messageUpdatedSubscription = undefined;
    }

    if (!this.messageUpdatedSubscription) {
      this.messageUpdatedSubscription = new MessageUpdatedSubscription(this, {
        variables: {
          subscribeMessageInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      });

      this.messageUpdatedSubscription.process(this.handleMessageUpdated);
      this.messageUpdatedSubscription.StartSubscription();
    }
  };

  SubscribePatch = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.patchSubscription) {
      this.patchSubscription.CompleteSubscription();
    }
    this.patchSubscription = new PatchSubscription(this, {
      variables: {
        subscribePatchInput: {
          contextId: this.variables.contextId,
          resourceId: this.variables.resourceId,
          referenceId: this.variables.referenceId,
        },
      },
    });
    this.patchSubscription.process(this.handlePatch);
    this.patchSubscription.StartSubscription();
  };

  @action ConnectMessageSubscription = () => {
    this.SubscribeMessageCreation();
    this.SubscribeMessageUpdate();
    this.SubscribeMessageDeletion();
  };

  @action handleAnnotations = result => {
    if (!result?.annotations) return;
    this.annotations = result.annotations;
    // - wait until viewer and notepad are ready before applying patches
    const whenDisposer = when(
      () =>
        !!(
          this.viewerControl &&
          // - I added viewerVisible here because cadReady can be true,
          // but the file we loaded is not the file for which we are
          // querying annotations here, so by checking viewerVisible &&
          // cadReady we're making sure we only attempt to add annotations
          // to the wasm once both viewer has been made visble and the
          // cadFile was loaded
          this.viewerControl.viewerVisible &&
          this.viewerControl.viewerReady &&
          this.viewerControl.cadReady
        ),
      () => {
        try {
          this.annotations.forEach(patchData => {
            this.applyPatch({
              type: 'annotation',
              action: 'add',
              data: patchData,
            });
          });
        } catch (e) {
          analytics.logError(e);
        }

        this.SubscribePatch();

        this.annotationsQuery.cancel();

        // - I always run into problems where I delete an annotation, and then it is
        // added right back, I always struggled with debugging this
        // - I found I can do this to basicly lock the result (which is what changes
        // and then it triggers a re-run of this function) to see where exactly
        // in the code the result changes, I am leaving this in here as future reference
        // for me, I often have problems with this annotations code
        // Object.defineProperty(this.annotationsQuery, 'result', {
        //   writable: false,
        // });
      },
    );
    this.disposers.push(whenDisposer);
  };

  QueryAnnotations = () => {
    if (!this.variables.contextId || !this.variables.resourceId) return;
    this.annotationsQuery = new AnnotationsQuery(this, {
      fetchPolicy: 'network-only',
      queryKey: 'resource-annotations',
      onSysErr: this.onQueryAnnotationsError,
      variables: {
        getAnnotationsInput: {
          contextId: this.variables.contextId,
          resourceId: this.variables.resourceId,
          referenceId: this.variables.referenceId,
        },
      },
    });
    this.annotationsQuery.process(x => x, this.handleAnnotations);
    this.annotationsQuery.query();
  };

  @action handleActiveParticipants = activeParticipants => {
    this.SubscribeParticipantStatus();
    if (!activeParticipants?.length) return;
    this.memberStatus = {};
    activeParticipants.forEach(activityData => {
      this.memberStatus[activityData.userId] = activityData;
    });
  };

  QueryActiveParticipants = () => {
    if (!this.variables.contextId || !this.variables.resourceId) return;
    this.activeParticipantsQuery = new ActiveParticipantStatusQuery(this, {
      queryKey: 'active-participants',
      onSysErr: null,
      variables: {
        contextId: this.variables.contextId,
        resourceId: this.variables.resourceId,
      },
    });
    this.activeParticipantsQuery.process(this.handleActiveParticipants);
    this.activeParticipantsQuery.query();
  };

  SendMessage = body => {
    const tempKey = `temp-${uuid()}`;
    const currentTime = new Date();
    const createdById = this.clientStore?.signedInProfile?.user?.id;
    const profile = {
      avatar: this.clientStore?.signedInProfile?.avatar,
      fullName: this.clientStore?.signedInProfile?.fullName,
      slug: this.clientStore?.signedInProfile?.fullName,
    };
    const createdByObject = {
      id: createdById,
      profile,
    };
    const localMessage = {
      body,
      messageType: 'regular',
      timestamp: currentTime,
      createdBy: createdByObject,
      // TODO: alex add extra param
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      tempKey,
    };

    // Add message to the UI at the bottom, until actual response comes back
    this.messages.push(localMessage);
    this.lastMessage = localMessage;
    this.lastReadMessage = localMessage;

    const options = {
      onSuccess: result => this.onSendMessageSuccess(result),
      onSysErr: error => {
        this.onSendMessageError(tempKey, error);
      },
      onUserErr: error => {
        this.onSendMessageError(tempKey, error);
      },
    };
    const input = new Input({
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      referenceId: this.variables.referenceId,
      sendAsId: this.sendAsId,
      tempKey,
      body,
    });
    const mutation = CreateMessageMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  UpdateMessage = (messageId, body) => {
    const options = {
      onSuccess: result => this.onUpdateMessageSuccess(result),
      onSysErr: this.onUpdateMessageError,
      onUserErr: this.onUpdateMessageError,
    };

    const input = new Input({
      messageId,
      body,
    });

    UpdateMessageMutation.New(input, options);
  };

  DeleteMessage = (messageId, optionsParameter) => {
    this.evictAnnotationAndMessage(null, messageId);

    const options = optionsParameter || {
      onSuccess: this.onDeleteMessageSuccess,
      onSysErr: this.onDeleteMessageError,
      onUserErr: this.onDeleteMessageError,
    };
    const input = new Input({
      messageId,
    });
    const mutation = DeleteMessageMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  SendAnnotation = ({
    body,
    tempKey,
    target,
    camera,
    optionsParameter,
    supportTargets,
    kind,
    jsonBlob,
  }) => {
    const options = optionsParameter || {
      onSuccess: this.onSendAnnotationSuccess,
      onSysErr: this.onSendAnnotationError,
      onUserErr: this.onSendAnnotationError,
    };
    this.loading = true;

    const targetToInput = t => {
      return {
        targetType: t.targetType,
        selType: t.selType,
        instanceIx: t.instanceIx,
        brepIx: t.brepIx,
        bodyIx: t.bodyIx,
        prcPath: t.prcPath,
        faceHit: t.faceHit,
        faceIx: Number.isInteger(t.faceIx) ? t.faceIx : undefined,
        loopIx: Number.isInteger(t.loopIx) ? t.loopIx : undefined,
        edgeIx: Number.isInteger(t.edgeIx) ? t.edgeIx : undefined,
        pointHit: t.pointHit,
        pointIx: Number.isInteger(t.pointIx) ? t.pointIx : undefined,
        topoIx: Number.isInteger(t.topoIx) ? t.topoIx : undefined,
        triangleHit: t.triangleHit,
        triangleIx: Number.isInteger(t.triangleIx) ? t.triangleIx : undefined,
        trianglePt: t.trianglePt || undefined,
        triangleNr: t.triangleNr || undefined,
        cadId: t.cadId || undefined,
        cadFileId: t.cadFileId || undefined,
        camera: t.camera || undefined,
      };
    };

    const annotationTargetInput = targetToInput(target);
    const annotationSupportTargetInputs = [];
    if (supportTargets) {
      for (let i = 0; i < supportTargets.length; i++) {
        const supportTarget = supportTargets[i];
        const supportTargetInput = targetToInput(supportTarget);
        annotationSupportTargetInputs.push(supportTargetInput);
      }
    }

    const input = new Input({
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      referenceId: this.variables.referenceId,
      tempKey,
      body,
      target: annotationTargetInput,
      supportTargets: annotationSupportTargetInputs,
      camera,
      kind,
      jsonBlob,
    });
    const mutation = CreateAnnotationMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  // - I wrote this as a helper for evictAnnotationAndMessage
  // - it finds the message for an id by checking if either message.id or
  // message.annotationId matches
  findMessageForIdOrAnnotationId = id => {
    if (!id) {
      return null;
    }
    for (let i = 0; i < this.messages.length; i++) {
      if (this.messages[i].id === id || this.messages[i].annotationId === id) {
        return this.messages[i];
      }
    }
  };

  // - I wrote this so I can evict annotation + its message
  // - when passing in annotationId === null this will evict the message,
  // and if the message has an annotationId, it will then also evict the
  // annotation for the message, otherwise only the message is evicted
  evictAnnotationAndMessage = (annotationId, messageId) => {
    const { cache } = apolloClient;

    let annotationIdFromMessage = annotationId;
    let messageIdFromMessage = messageId;

    // - if we have both messageId and annotationId we can just skip this
    // and go on to the evictions, otherwise we try to get try to find the
    // message and then use the annotionId and messageId from that to make
    // sure we have both ids that we need to evict
    if (!annotationId || !messageId) {
      const message = this.findMessageForIdOrAnnotationId(
        messageId || annotationId,
      );
      if (message) {
        annotationIdFromMessage = message.annotationId;
        messageIdFromMessage = message.id;
      }
    }

    if (messageIdFromMessage) {
      cache.evict({ id: `Message:${messageIdFromMessage}` });
    }

    // - if we have an annotationId (or have it FromMessage), then evict it
    if (annotationIdFromMessage) {
      cache.evict({ id: `Annotation:${annotationIdFromMessage}` });
    }

    cache.gc();
  };

  DeleteAnnotation = (annotationId, optionsParameter) => {
    this.evictAnnotationAndMessage(annotationId, null);

    this.loading = true;
    const options = optionsParameter || {
      onSuccess: this.onDeleteAnnotationSuccess,
      onSysErr: this.onDeleteAnnotationError,
      onUserErr: this.onDeleteAnnotationError,
    };
    const input = new Input({ annotationId });
    const mutation = DeleteAnnotationMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  QueryConversation = () => {
    if (!this.variables.contextId || !this.variables.resourceId) {
      return;
    }

    let Query = ContextConversationQuery;
    const { name } = clientStore.routerStore.route;
    const canPickParticipants = ['rfq', 'order'].some(v => name.includes(v));

    if (this.clientStore.hasRole('INTERMEDIATOR') && canPickParticipants) {
      Query = ConversationQueryWithParticipants;
    }

    this.superConversationQuery = new Query(this, {
      fetchPolicy: 'network-only',
      onSysErr: this.onQueryConversationError,
      variables: {
        contextId: this.variables.contextId,
        resourceId: this.variables.resourceId,
        referenceId: this.variables.referenceId,
        first: messagesPageSize,
      },
    });
    this.superConversationQuery.process(x => x, this.handleConversation);
    this.superConversationQuery.query();
  };

  onQueryConversationError = () => {
    console.error('error when querying conversation!!');
  };

  @action handleConversation = result => {
    this.ConnectMessageSubscription();

    if (!result) {
      return;
    }

    // This is to discard converations being pushed by Apollo cache
    // that are no longer relevant.
    if (result.resourceId !== this.variables.resourceId) {
      return;
    }

    this.associatedSpaceId = result.space?.id;
    // If the associated space hasn't been resolved, it might be
    // due to flask taking to long to resolve.
    if (!this.associatedSpaceId) {
      analytics.sendEvent(
        'error-space-not-found-in-conversation',
        { missingSpaceForRfq: this.contextAppHolder?.rfqId },
        { ga: true },
      );
    }

    if (result.participants) {
      this.participants = result.participants;
    }
    // If there are no messages yet in the conversation,
    // result will be null. However, we still want to
    // subscribe to message creation and deletion
    if (result?.messages && this.messages.length === 0) {
      this.lastReadMessage = result.lastReadMessage;
      this.lastMessage = result.lastMessage;
      this.unreadCount = result.unreadCount;

      this.firstMessageIndex = Math.max(
        result.messages.total - result.messages.nodes.length,
        0,
      );

      this.lastMessageIndex = Math.max(result.messages.total - 1, 0);

      if (this.messageQueryManager) {
        this.messageQueryManager.dispose();
      }
      this.messageQueryManager = new BidirectionalQueryManager({
        startOffset: result.messages.total - result.messages.nodes.length,
        pageSize: messagesPageSize,
        contextId: this.variables.contextId,
        resourceId: this.variables.resourceId,
        referenceId: this.variables.referenceId,
        onPrevResultCb: r => {
          // Here I will receive the new bunch of messages
          let newMessages = r?.messages?.nodes;
          if (newMessages && newMessages.length > 0) {
            // Update the first message index (required for virtuoso)
            this.firstMessageIndex = Math.max(
              (this.firstMessageIndex -= newMessages.length),
              0,
            );

            // Warning! We are filtering here the messages because
            // for some unclear reason, the queries appears duplicated
            newMessages = newMessages.filter(
              msg => !this.messages.some(m => m.id === msg.id),
            );
            if (newMessages.length > 0) {
              this.messages = [...newMessages, ...this.messages];
            }
          }
        },
      });

      const processedIds = [];

      for (let i = 0; i < result.messages.nodes.length; i += 1) {
        const message = result.messages.nodes[i];
        const { createdBy } = message;
        const { id } = createdBy;

        if (!processedIds.includes(id)) {
          processedIds.push(id);

          this.members[id] = createdBy.profile;
        }
      }
      // ignore the messages that is already there
      this.messages = result.messages.nodes.filter(
        msg => !this.messages.some(m => m.id === msg.id),
      );
    }
  };

  @action setReadMessage = messageId => {
    if (!messageId) return;

    const options = {
      onSuccess: () => {
        if (this.lastReadMessage?.id !== messageId) {
          this.lastReadMessage = { id: messageId };
        }
      },
      onSysErr: this.onSetMessageReadError,
      onUserErr: this.onSetMessageReadError,
    };
    const input = new Input({
      messageId,
    });
    const mutation = ReadMessageMutation.New(input, options);
    this.mutations.push(mutation);
  };

  @action resendMessage = message => {
    const options = {
      onSuccess: result => {
        this.onResendMessageSuccess(result);
        this.onSendMessageSuccess(result);
      },
      onSysErr: error => {
        this.onSendMessageError(message.tempKey, error);
      },
      onUserErr: error => {
        this.onSendMessageError(message.tempKey, error);
      },
    };

    const messageFailed = this.failedMessages.find(
      failedMessage => message.tempKey === failedMessage.tempKey,
    );

    if (messageFailed) {
      messageFailed.resending = true;
    }

    const input = new Input({
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      referenceId: this.variables.referenceId,
      sendAsId: this.sendAsId,
      tempKey: message.tempKey,
      body: message.body,
    });
    const mutation = CreateMessageMutation.New(input, options);
    this.mutations.push(mutation);
  };

  @action deleteSendFailedMessage = tempKey => {
    this.failedMessages = this.failedMessages.filter(
      failedMessage => failedMessage.tempKey !== tempKey,
    );
    this.messages = this.messages.filter(
      message => message.tempKey !== tempKey,
    );
  };

  onSetMessageReadError = () => {
    console.error('error saving last read message status!!');
  };

  LoadPreviousMessageBatch = () => {
    if (!this.messageQueryManager) return;
    this.messageQueryManager.queryPreviousPage();
  };

  onQueryMessagesError = () => {
    console.error('error when querying messages!!');
  };
}
